import { getDefaultStore } from "jotai";
import { spinnerAtom } from "@/atoms/spinner.atom";
import { ApiClientError } from "./api-error";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiClientOptions extends Omit<RequestInit, "body"> {
  next?: {
    revalidate?: number;
    tags?: string[];
  };
}

async function request<TResponse, TBody = undefined>(
  method: HttpMethod,
  url: string,
  body?: TBody,
  options: ApiClientOptions = {},
): Promise<TResponse> {
  const store = getDefaultStore();
  store.set(spinnerAtom, true);
  const isFormData = body instanceof FormData;

  const headers = new Headers(options.headers);
  if (isFormData) {
    headers.delete("Content-Type");
  } else {
    headers.set("Content-Type", "application/json");
  }

  const requestBody =
    body === undefined
      ? undefined
      : isFormData
        ? (body as unknown as FormData)
        : JSON.stringify(body);

  let res: Response;
  try {
    res = await fetch(`/api${url}`, {
      method,
      body: requestBody,
      headers,
      next: options.next,
      cache: options.cache,
      credentials: options.credentials,
    });
  } finally {
    store.set(spinnerAtom, false);
  }

  let data: unknown | ApiErrorResponse;
  const contentType = res.headers.get("content-type");

  if (contentType?.includes("application/json")) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    throw new ApiClientError(res.status, data as ApiErrorResponse);
  }

  return data as TResponse;
}

export const apiClient = {
  get: <TResponse>(url: string, options?: ApiClientOptions) =>
    request<TResponse>("GET", url, undefined, options),

  post: <TResponse, TBody>(
    url: string,
    body: TBody,
    options?: ApiClientOptions,
  ) => request<TResponse, TBody>("POST", url, body, options),

  put: <TResponse, TBody>(
    url: string,
    body: TBody,
    options?: ApiClientOptions,
  ) => request<TResponse, TBody>("PUT", url, body, options),

  patch: <TResponse, TBody>(
    url: string,
    body: TBody,
    options?: ApiClientOptions,
  ) => request<TResponse, TBody>("PATCH", url, body, options),

  delete: <TResponse>(url: string, options?: ApiClientOptions) =>
    request<TResponse>("DELETE", url, undefined, options),
};
